import { api, ErroreApi } from './api.ts'
import { IMPRONTA_SCRIPT_COLLEGAMENTI, SCRIPT_COLLEGAMENTI_MESSAGGIO } from '@cidis/shared/script-collegamenti'

/**
 * Webmail: forme dei dati, chiamate all'API e ripulitura del corpo HTML dei messaggi.
 *
 * Le rotte stanno tutte sotto /posta e rispondono 409 INTERRUTTORE_SPENTO finche' la
 * funzione 'webmail_completa' non e' attiva: ogni chiamata di questo modulo puo' fallire
 * cosi', e l'interfaccia lo distingue da un errore vero con interruttoreSpento().
 *
 * Fuori dal perimetro l'API risponde 404 e non 403: qui un 404 non significa "vietato",
 * significa che per questo utente l'oggetto non esiste, e va mostrato come tale.
 */

export type AmbitoCasella = 'organizzazione' | 'utente' | 'gruppo' | 'progetto'

/** Livello su una casella condivisa (504): «legge» apre e basta, «scrive» risponde, invia, archivia. */
export type LivelloCasella = 'legge' | 'scrive'
export type StatoThread = 'aperto' | 'archiviato' | 'cestinato'
export type DirezioneMessaggio = 'entrata' | 'uscita'
export type EsitoAntivirus = 'in_attesa' | 'pulito' | 'infetto' | 'non_scansionato'

export type CasellaWebmail = {
  id: string
  ambito: AmbitoCasella
  indirizzo: string
  nome_visualizzato: string
  /**
   * Accesso pieno: vero sulla casella propria accesa e sulle caselle condivise (di gruppo e di progetto) da
   * cui si scrive. Falso quando la casella si legge in forza di una supervisione, e' quella dell'ente, e'
   * condivisa in sola lettura o e' la propria spenta: da qui l'interfaccia non propone di scrivere.
   */
  mia: boolean
  /** Casella spenta (3/11/2026): la propria si consulta soltanto, con la scritta «Casella spenta: non spedisce». */
  spenta?: boolean
  /**
   * Il campo «Da» la propone (3/11/2026): accesso pieno e un modo di spedire. Falso sulle caselle spente e su
   * quelle IMAP senza server di invio; `non_spedisce` dice perche'.
   */
  spedisce?: boolean
  non_spedisce?: string | null
  /** Casella di progetto (504): il progetto. */
  progetto: { id: string; titolo: string; codice_cidis: string | null } | null
  /** Casella condivisa (504): il livello di chi guarda. */
  livello: LivelloCasella | null
  /** La ricezione del gestionale ha gia' consegnato posta: falso prima del passaggio dell'MX. */
  ricezione_attiva: boolean
  /** Casella attiva con la lettura configurata: «Aggiorna ora» ha qualcosa da leggere. */
  lettura: boolean
  /**
   * Casella ospitata (502): la posta arriva da sola, senza «Aggiorna ora». Con l'ora dell'ultimo
   * messaggio ricevuto per l'indicatore «Ricezione».
   */
  ospitata: boolean
  ultimo_arrivo_il: string | null
  supervisione: { id: string; scade_il: string } | null
  /**
   * Messaggi da leggere della casella (2/11/2026), per il selettore in cima a Posta: non letti delle
   * cartelle tranne inviata, bozze, cestino e indesiderata. Null sulle caselle in supervisione.
   */
  non_letti: number | null
  /**
   * Canali salvati con la sicurezza «nessuna», prima che non fosse piu' ammessa (498): l'API non
   * invia e non legge da li' finche' la configurazione non si corregge.
   */
  cifratura_assente: ('invio' | 'lettura')[]
}

export type Cartella = {
  id: string
  nome: string
  tipo: 'in_arrivo' | 'inviata' | 'bozze' | 'cestino' | 'indesiderata' | 'archivio' | 'altra'
  percorso: string
  messaggi: number
  non_letti: number
  /** Cartella personale sotto cui sta (541). */
  madre_id: string | null
  /** Cartella personale (541): si rinomina e si elimina. Solo sulle caselle ospitate. */
  personale: boolean
  /** Ci si spostano conversazioni (541): in arrivo, archivio, indesiderata, personali; solo sulle ospitate. */
  destinazione: boolean
}

export type VoceThread = {
  id: string
  /** La casella che ha ricevuto la conversazione: nella vista unica ne da' l'etichetta. */
  casella_id: string
  oggetto: string
  anteprima: string
  partecipanti: string[]
  messaggi: number
  non_letti: number
  ultimo_il: string
  ha_allegati: boolean
  persona_id: string | null
  stato: StatoThread
  /** Ingresso nel cestino (535); null fuori dal cestino. */
  cestinato_il: string | null
  /** Chi ha aperto la conversazione per ultimo, sulle caselle di gruppo e di progetto. */
  aperto_da: { id: string; nome: string; cognome: string } | null
  aperto_il: string | null
  /** Avviso dell'elenco (522): il peggiore fra i messaggi arrivati. */
  provenienza: 'falsa_cidis' | 'esterna_non_verificata' | 'esterna' | null
  /** Sulle caselle di gruppo e di progetto: chi ha mandato l'ultimo messaggio in uscita. */
  inviato_da: UtenteInviante | null
  /** Un messaggio con la priorita' alta (539): «!» accanto all'oggetto. */
  priorita_alta?: boolean
  /** Messaggi non partiti (3/11/2026): «Non inviato» in rosso. */
  non_inviati?: number
  /** Con la ricerca: le cartelle dei messaggi della conversazione. */
  cartelle?: CartellaTrovata[]
} & SegniThread

/** `ordinate_recenti` (541): l'ordine per mittente o oggetto vale sulle ultime N conversazioni. */
export type PaginaThread = { thread: VoceThread[]; prossimo_cursore: string | null; ordinate_recenti?: number | null }

export type Allegato = {
  id: string
  nome: string
  mime: string
  dimensione: number
  antivirus: EsitoAntivirus
  /** Partito come collegamento pubblico (509): stato e numero degli scaricamenti. */
  collegamento: { id: string; revocato_il: string | null; scade_il: string | null; scaricamenti: number } | null
  /** In quarantena (546): niente comando per scaricarlo, la scritta o chi l'ha eliminato. */
  quarantena?: QuarantenaAllegato | null
}

/** Lo stato di quarantena di un allegato (546), come lo manda l'API. */
export type QuarantenaAllegato = { messa_il: string; eliminata_il: string | null; eliminata_da: string | null }

/** La scritta sull'allegato in quarantena, uguale in ogni pagina (come nell'API). */
export const SCRITTA_QUARANTENA = 'Infetto, in quarantena: non si scarica'

/** «Infetto, in quarantena: non si scarica», o «Infetto: eliminato dalla quarantena il … da …». */
export function scrittaQuarantena(q: QuarantenaAllegato): string {
  if (!q.eliminata_il) return SCRITTA_QUARANTENA
  const il = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
    .format(new Date(q.eliminata_il))
  return `Infetto: eliminato dalla quarantena il ${il}${q.eliminata_da ? ` da ${q.eliminata_da}` : ''}`
}

export type Messaggio = {
  id: string
  /** Identificativo RFC 5322: e' il valore da citare per rispondere dentro lo stesso thread. */
  message_id: string | null
  direzione: DirezioneMessaggio
  da: string
  a: string[]
  cc: string[]
  oggetto: string
  data: string
  letto_il: string | null
  testo: string | null
  html: string | null
  allegati: Allegato[]
  /** Spedito a scadenza (510): «messaggio» per i destinatari esterni, oppure solo gli allegati. */
  scadenza: { modo: ModoScadenza; scade_il: string; revocata_il: string | null } | null
  /** Da dove viene davvero (522): decisa dai controlli del server di ricezione, non dal mittente scritto. */
  provenienza: Provenienza | null
  /** In uscita da una casella di gruppo o di progetto: chi l'ha mandato. Solo nel gestionale. */
  inviato_da: UtenteInviante | null
  /** Priorita' alta (539). Assente fuori dalla webmail. */
  priorita_alta?: boolean
  /** Conferma di lettura chiesta dal mittente (539), sui messaggi arrivati. */
  conferma_lettura?: ConfermaChiesta | null
  /** Sui messaggi inviati (539): se si e' chiesta la conferma e le conferme arrivate. */
  conferme_ricevute?: { chiesta: boolean; ricevute: ConfermaRicevuta[] } | null
  /**
   * In uscita dal gestionale (3/11/2026): 'errore' e' un messaggio non partito, che la conversazione mostra con
   * «Non inviato», il motivo, «Riprova» e «Modifica»; 'in_coda' mentre parte.
   */
  stato_invio?: 'bozza' | 'in_coda' | 'inviato' | 'errore' | null
  /** Il motivo del mancato invio, come lo ha ridotto l'API (es. «SMTP 550 5.1.1»). */
  errore?: string | null
  /** La bozza che ricorda il tentativo: «Modifica» la riapre. */
  bozza_id?: string | null
}

/**
 * Il motivo del mancato invio in una frase (3/11/2026). L'API conserva solo il codice di risposta del server, o
 * una classificazione fissa del guasto (erroreSmtpRidotto): qui diventa la frase per chi ha scritto.
 */
export function motivoNonInviato(errore: string | null | undefined): string {
  if (!errore) return 'il server di posta non ha accettato il messaggio'
  const codice = /^SMTP ([245]\d{2})(?: ([245]\.\d{1,3}\.\d{1,3}))?$/.exec(errore)
  if (codice) {
    const quale = `codice ${codice[1]}${codice[2] ? ` ${codice[2]}` : ''}`
    return codice[1]!.startsWith('4')
      ? `il server di posta ha rimandato l’invio (${quale}): di solito basta riprovare fra qualche minuto`
      : `il server di posta ha rifiutato il messaggio (${quale})`
  }
  const frasi: Record<string, string> = {
    'SMTP nessuna risposta': 'il server di posta non risponde',
    'SMTP host non risolto': 'il server di posta della casella non si trova',
    'SMTP connessione interrotta': 'la connessione con il server di posta non è riuscita',
    'SMTP TLS non valido': 'il certificato del server di posta non è valido',
    'SMTP autenticazione rifiutata': 'il server di posta ha rifiutato nome utente o password della casella',
    'SMTP errore': 'il server di posta non ha accettato il messaggio',
  }
  return frasi[errore] ?? errore
}

/** «Riprova» su un messaggio non partito: la stessa riga e lo stesso Message-ID (3/11/2026). */
export const riprovaInvio = (postaId: string) =>
  api.post<{ id: string; thread_id: string; stato_invio: 'inviato' | 'errore'; errore: string | null; bozza_id: string }>(
    `/posta/messaggi/${postaId}/riprova`)

/** «Modifica» su un messaggio non partito: la bozza che lo ricorda, o una nuova con il suo contenuto. */
export const modificaInvio = (postaId: string) => api.post<{ bozza_id: string }>(`/posta/messaggi/${postaId}/modifica`)

export type ConfermaChiesta = {
  /** L'indirizzo a cui andrebbe la conferma (Disposition-Notification-To). */
  a: string
  esito: 'inviata' | 'ignorata' | null
  decisa_il: string | null
  /** «Invia la conferma» e «Ignora» si mostrano: lo decide l'API (chi scrive, non impersonando, non spam). */
  proponibile: boolean
}

export type ConfermaRicevuta = { lettore: string; disposizione: string; ricevuta_il: string }

/** Chi ha mandato un messaggio da una casella condivisa (2/11/2026). */
export type UtenteInviante = { id: string; nome: string; cognome: string }

export type Provenienza =
  'interna' | 'automatica' | 'cidis' | 'esterna' | 'esterna_non_verificata' | 'falsa_cidis' | 'non_verificata'

/**
 * La fascia sopra il messaggio (522). Il mittente scritto si falsifica: queste frasi dicono che cosa hanno
 * visto i controlli del server di ricezione (DMARC, DKIM, SPF) o la consegna interna del gestionale.
 */
export const FASCE_PROVENIENZA: Record<Provenienza, { tono: 'positivo' | 'neutro' | 'attenzione' | 'critico'; titolo: string; testo: string }> = {
  interna: { tono: 'positivo', titolo: 'Da CIDIS, verificato',
    testo: 'Consegnato dentro il gestionale da un collega: non è passato da nessun server esterno.' },
  automatica: { tono: 'neutro', titolo: 'Comunicazione automatica del gestionale',
    testo: 'Messaggio automatico del gestionale CIDIS, spedito dai server autorizzati. Non rispondere a questo indirizzo.' },
  cidis: { tono: 'positivo', titolo: 'Da CIDIS, verificato',
    testo: 'Il server di ricezione ha confermato che è partito dai server di posta di CIDIS.' },
  esterna: { tono: 'attenzione', titolo: 'Esterno a CIDIS',
    testo: 'Viene da fuori CIDIS. Attenzione a collegamenti, allegati e richieste di pagamento o di credenziali.' },
  esterna_non_verificata: { tono: 'attenzione', titolo: 'Esterno a CIDIS, mittente non verificato',
    testo: 'Viene da fuori CIDIS e il dominio del mittente non conferma di averlo spedito: il mittente potrebbe non '
      + 'essere chi dice. Non aprire collegamenti o allegati se non lo aspettavi.' },
  falsa_cidis: { tono: 'critico', titolo: 'Si presenta come CIDIS ma non viene dai server di CIDIS',
    testo: 'Possibile truffa: il mittente scritto è un indirizzo CIDIS, ma il messaggio è partito da un altro server. '
      + 'Non rispondere, non aprire collegamenti o allegati, non fare pagamenti: avvisa la Direzione.' },
  non_verificata: { tono: 'neutro', titolo: 'Provenienza non verificata',
    testo: 'Arrivato prima dei controlli di provenienza, o letto da un altro server: il mittente non è di CIDIS.' },
}

export type ThreadAperto = {
  thread: {
    id: string
    /** La casella che ha ricevuto la conversazione: la risposta parte da li'. */
    casella_id: string
    oggetto: string
    persona_id: string | null
    intervento_id: string | null
    stato: StatoThread
  } & SegniThread
  messaggi: Messaggio[]
}

// --- bandierine ed etichette (538) ------------------------------------------------------------------

/**
 * Bandierine ed etichette delle conversazioni (richiesta di Carmem, 2/11/2026). La bandierina e' una per
 * conversazione «per tutti» quelli che usano la casella e una «solo per te»; le etichette sono testo, per
 * tutti (della casella) o personali (tue, su tutte le caselle che leggi). Stanno solo nel gestionale: i
 * programmi di posta collegati all'IMAP non le vedono.
 */
export type Colore = 'rosso' | 'arancione' | 'giallo' | 'verde' | 'blu' | 'viola'

/** I sei colori nell'ordine dei menu, con il nome detto della bandierina («bandierina rossa»). */
export const COLORI: { valore: Colore; nome: string }[] = [
  { valore: 'rosso', nome: 'rossa' }, { valore: 'arancione', nome: 'arancione' },
  { valore: 'giallo', nome: 'gialla' }, { valore: 'verde', nome: 'verde' },
  { valore: 'blu', nome: 'blu' }, { valore: 'viola', nome: 'viola' },
]

export const nomeColore = (c: Colore) => COLORI.find((x) => x.valore === c)?.nome ?? c

export type Per = 'tutti' | 'me'

export type EtichettaSuThread = { id: string; nome: string; per: Per }

export type SegniThread = {
  /** La bandierina per tutti, con chi l'ha messa. */
  bandierina: { colore: Colore; messa_da: { nome: string; cognome: string } | null } | null
  /** La tua, «solo per te». */
  bandierina_personale: Colore | null
  etichette: EtichettaSuThread[]
}

export type EtichettaPosta = {
  id: string
  nome: string
  per: Per
  /** La casella di quella per tutti; null sulle tue. */
  casella_id: string | null
}

/** Filtro per bandierina: di qualsiasi colore o di uno. */
export type FiltroBandierina = 'qualsiasi' | Colore

/** Lunghezza massima del nome di un'etichetta (lo controlla anche l'API). */
export const NOME_ETICHETTA_MASSIMO = 40

/** Le etichette della casella e le tue; senza casella, quelle della vista «Tutte le caselle». */
export const elencaEtichette = (casellaId: string | null, automatica = false) =>
  api.get<{ etichette: EtichettaPosta[] }>(casellaId ? `/posta/caselle/${casellaId}/etichette` : '/posta/tutte/etichette',
    { automatica }).then((r) => r.etichette)

export const creaEtichetta = (nome: string, per: Per, casellaId: string | null) =>
  api.post<EtichettaPosta>('/posta/etichette', { nome, per, casella_id: per === 'tutti' ? casellaId : null })

export const rinominaEtichetta = (id: string, nome: string) =>
  api.patch<{ id: string; nome: string }>(`/posta/etichette/${id}`, { nome })

export const eliminaEtichetta = (id: string) =>
  api.elimina<{ eliminata: boolean; conversazioni: number }>(`/posta/etichette/${id}`)

/** Mette, cambia o toglie (colore nullo) la bandierina per tutti o la tua. */
export const mettiBandierina = (threadId: string, per: Per, colore: Colore | null) =>
  api.post<SegniThread>(`/posta/thread/${threadId}/bandierina`, { per, colore })

export const mettiEtichetta = (threadId: string, etichettaId: string, messa: boolean) =>
  api.post<SegniThread>(`/posta/thread/${threadId}/etichette`, { etichetta_id: etichettaId, messa })

/**
 * Supervisione su una casella altrui, come la restituisce GET /posta/supervisioni
 * (servizi/webmail.ts, SupervisioneEsposta): da entrambi i lati, chi legge e chi e' letto.
 */
export type UtenteBreve = { id: string; nome: string; cognome: string }

export type Supervisione = {
  id: string
  casella_id: string
  casella_indirizzo: string
  /** Il lato di chi guarda: chi legge la casella o chi ne e' il titolare. */
  ruolo: 'supervisore' | 'sorvegliato'
  supervisore: UtenteBreve
  titolare: UtenteBreve | null
  motivazione: string
  solo_thread_id: string | null
  inizio: string
  scade_il: string
  revocata_il: string | null
  /** Non revocata e non scaduta. */
  attiva: boolean
}

/** Casella personale di un altro operatore, fra quelle di cui si puo' chiedere la lettura. */
export type CasellaSorvegliabile = {
  id: string
  indirizzo: string
  nome_visualizzato: string
  titolare: { nome: string; cognome: string }
}

/**
 * Richiesta di supervisione fatta da chi guarda (`supervisione_posta`): in attesa, concessa e non
 * ancora usata, oppure respinta da poco. Quelle usate sono gia' supervisioni.
 */
export type RichiestaSupervisione = {
  id: string
  casella_id: string
  casella_indirizzo: string | null
  titolare: { nome: string; cognome: string } | null
  motivazione: string
  /** Giorni di lettura chiesti e approvati. Null sulle richieste fatte senza durata. */
  giorni: number | null
  stato: 'in_attesa' | 'approvata' | 'respinta'
  richiesta_il: string
  scade_il: string
  decisa_il: string | null
  nota_decisione: string | null
}

export type FiltriThread = {
  cartella?: string | null
  cerca?: string
  /** Dove si cerca (3/11/2026): la cartella, tutte le cartelle della casella, tutte le caselle. */
  ambito?: AmbitoRicerca
  stato?: StatoThread
  persona_id?: string
  /** «Solo non lette»: le conversazioni con almeno un messaggio da leggere (lo filtra l'API). */
  non_letti?: boolean
  /** Per bandierina e per etichetta (538): in tutte le cartelle della casella, la cartella non vale. */
  bandierina?: FiltroBandierina | null
  etichetta?: string | null
  /** Ordine (541): con la ricerca vale la data. */
  ordine?: 'data' | 'mittente' | 'oggetto' | 'dimensione'
  verso?: 'asc' | 'desc'
  limite?: number
  prima_di?: string | null
}

/** Dove si cerca: «Questa cartella», «Tutte le cartelle» (senza Indesiderata e Cestino), «Tutte le caselle». */
export type AmbitoRicerca = 'cartella' | 'casella' | 'caselle'

/** La cartella di un risultato della ricerca. */
export type CartellaTrovata = { id: string; nome: string; tipo: string }

/** «Tutte le caselle» oppure una casella alla volta: la scelta resta salvata per la persona. */
export type VistaPosta = { modo: 'tutte' | 'casella'; casella_id: string | null }

export type AllegatoDaInviare = { nome: string; mime: string; dimensione: number; storage_key: string }

export type MessaggioDaInviare = {
  casella_id: string
  a: string[]
  cc?: string[]
  ccn?: string[]
  oggetto: string
  testo: string
  html?: string
  in_risposta_a?: string
  allegati?: AllegatoDaInviare[]
  bozza?: boolean
  scadenza?: ScadenzaDaInviare | null
  /** «Chiedi la conferma di lettura» e «Priorità alta» (539). */
  conferma_lettura?: boolean
  priorita_alta?: boolean
}

// --- email a scadenza (510) ------------------------------------------------------------------------

export type ModoScadenza = 'messaggio' | 'allegati'

/** Come la chiede il compositore. La password si comunica a voce e non si conserva. */
export type ScadenzaDaInviare = {
  modo: ModoScadenza
  scade_il: string
  password?: string | null
  mostra_oggetto?: boolean
}

export type EventoScadenza = {
  tipo: 'apertura' | 'password_errata' | 'blocco' | 'scaricamento' | 'revoca' | 'proroga'
  avvenuto_il: string; ip_troncato: string | null; chi: string | null; dettaglio: string | null
}

export type ScadenzaLetta = {
  modo: ModoScadenza; scade_il: string; revocata_il: string | null; password: boolean; mostra_oggetto: boolean
  eventi: EventoScadenza[]
}

export const leggiScadenza = (postaId: string) => api.get<ScadenzaLetta>(`/posta/messaggi/${postaId}/scadenza`)

export const prorogaScadenza = (postaId: string, scadeIl: string) =>
  api.post<{ scade_il: string }>(`/posta/messaggi/${postaId}/scadenza/proroga`, { scade_il: scadeIl })

export const revocaScadenza = (postaId: string) =>
  api.post<{ revocata: boolean }>(`/posta/messaggi/${postaId}/scadenza/revoca`)

/** «2026-11-08T17:30» per un campo data e ora, nell'ora del browser. */
export function perCampoDataOra(quando: Date): string {
  const z = (n: number) => String(n).padStart(2, '0')
  return `${quando.getFullYear()}-${z(quando.getMonth() + 1)}-${z(quando.getDate())}T${z(quando.getHours())}:${z(quando.getMinutes())}`
}

/** Fra `giorni` giorni, alla stessa ora. */
export const fraGiorni = (giorni: number) => perCampoDataOra(new Date(Date.now() + giorni * 86_400_000))

/** La scadenza come la legge chi ha spedito: «a scadenza fino al …», «scaduto per il destinatario», «revocato». */
export function statoScadenza(s: { scade_il: string; revocata_il: string | null }): string {
  if (s.revocata_il) return 'revocato per il destinatario'
  const fine = new Date(s.scade_il)
  return fine <= new Date() ? 'scaduto per il destinatario'
    : `a scadenza fino al ${fine.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}`
}

// --- allegati grandi come collegamento (509) ---------------------------------------------------

/**
 * Soglia oltre la quale gli allegati piu' grandi partono come collegamento, e dimensione massima di un
 * file, in byte. Finche' l'API non risponde vale il massimo predefinito.
 */
export type LimitiAllegati = { soglia_byte: number; massimo_byte: number }

export const LIMITI_PREDEFINITI: LimitiAllegati = { soglia_byte: 20 * 1024 * 1024, massimo_byte: 200 * 1024 * 1024 }

export const leggiLimitiAllegati = () => api.get<LimitiAllegati>('/posta/allegati/limiti')

/** Peso di un allegato nel messaggio: un terzo in piu' per la codifica, come nell'API. */
export const pesoNelMessaggio = (byte: number) => Math.ceil(byte * 4 / 3 * 1.03)

/**
 * Quali allegati partiranno come collegamento: la stessa regola di allegatiDaCollegare in
 * servizi/collegamenti.ts dell'API, che decide all'invio. Qui serve a dirlo prima.
 */
export function allegatiDaCollegare(allegati: { dimensione: number }[], sogliaByte: number): Set<number> {
  const ordine = allegati.map((a, i) => ({ i, peso: pesoNelMessaggio(a.dimensione) }))
    .sort((x, y) => y.peso - x.peso || y.i - x.i)
  let totale = ordine.reduce((somma, a) => somma + a.peso, 0)
  const collegati = new Set<number>()
  for (const a of ordine) {
    if (totale <= sogliaByte) break
    collegati.add(a.i)
    totale -= a.peso
  }
  return collegati
}

export type Scaricamento = { scaricato_il: string; ip_troncato: string | null }

export type CollegamentoLetto = {
  id: string; allegato_id: string; nome: string; dimensione: number
  creato_il: string; scade_il: string | null; revocato_il: string | null
  motivo_revoca: 'manuale' | 'invio_non_riuscito' | null
  scaricamenti: Scaricamento[]
}

export const collegamentiDelMessaggio = (postaId: string) =>
  api.get<CollegamentoLetto[]>(`/posta/messaggi/${postaId}/collegamenti`)

export const revocaCollegamento = (id: string) => api.post<{ revocato: boolean }>(`/posta/collegamenti/${id}/revoca`)

export type VoceRegistroCollegamenti = Omit<CollegamentoLetto, 'allegato_id'> & {
  posta_id: string; casella: string; mittente: string | null
}

export const registroCollegamenti = () => api.get<VoceRegistroCollegamenti[]>('/posta/collegamenti')

/**
 * Avviso in calce (498): il server lo aggiunge in fondo ai messaggi scritti dalle persone quando
 * `in_vigore` e' vero, cioe' con la webmail attiva e il testo non vuoto. Le email automatiche del
 * gestionale escono senza (499).
 */
export type StatoAvviso = { testo: string; in_vigore: boolean }

/**
 * Richiesta di cambiare il testo dell'avviso (499): la decide il Referente privacy o il DPO in
 * «Approvazioni»; fino ad allora vale il testo in vigore. In attesa, oppure l'ultima decisa da meno
 * di trenta giorni.
 */
export type RichiestaAvviso = {
  id: string
  stato: 'in_attesa' | 'approvata' | 'respinta'
  testo: string
  motivazione: string
  richiesta_il: string
  scade_il: string
  decisa_il: string | null
  nota_decisione: string | null
  richiedente: string
  decisore: string | null
}

/**
 * Supervisione in corso su una casella qualsiasi, per chi le concede e le revoca (Referente privacy
 * e DPO): GET /posta/supervisioni/tutte.
 */
export type SupervisioneInCorso = Omit<Supervisione, 'ruolo' | 'revocata_il' | 'attiva'> & {
  concessa_da: { nome: string; cognome: string } | null
}

/** Il testo delle bozze arriva con i campi «[DA COMPILARE: …]»: vanno scritti prima di usarlo. */
export const daCompilare = (testo: string) => /\[DA COMPILARE/i.test(testo)

// --- chiamate ------------------------------------------------------------------------------

/** `automatica`: la rilettura dei conteggi a tempo, che non rinnova la sessione. */
export const elencaCaselle = (automatica = false) => api.get<CasellaWebmail[]>('/posta/caselle', { automatica })

let dominiCidis: Promise<string[]> | null = null
/**
 * I domini di CIDIS (`accesso.domini`, GET /auth/domini): l'etichetta dei messaggi dello storico guarda il
 * dominio del mittente. Una lettura per pagina; dopo un errore si riprova alla richiesta successiva.
 */
export function leggiDominiCidis(): Promise<string[]> {
  if (!dominiCidis) {
    dominiCidis = api.get<{ domini: string[] }>('/auth/domini').then((r) => r.domini)
    dominiCidis.catch(() => { dominiCidis = null })
  }
  return dominiCidis
}

// --- rubrica (527) -------------------------------------------------------------------------------

export type Contatto = {
  id: string
  indirizzo: string
  nome: string | null
  invii: number
  ultimo_invio_il: string | null
  origine: 'invio' | 'storico' | 'manuale' | 'importato'
}
export type Collega = { nome: string; indirizzo: string }
/** Gruppo della rubrica (539): i contatti per identificativo. */
export type GruppoRubrica = { id: string; nome: string; contatti: string[] }
export type Rubrica = { contatti: Contatto[]; colleghi: Collega[]; gruppi?: GruppoRubrica[] }

export const leggiRubrica = (casellaId: string) => api.get<Rubrica>(`/posta/caselle/${casellaId}/rubrica`)
export const aggiungiContatto = (casellaId: string, indirizzo: string, nome: string | null) =>
  api.post<{ id: string }>(`/posta/caselle/${casellaId}/rubrica`, { indirizzo, nome })
export const correggiContatto = (id: string, nome: string | null) => api.patch<{ id: string }>(`/posta/rubrica/${id}`, { nome })
export const cancellaContatto = (id: string) => api.elimina<{ cancellato: boolean }>(`/posta/rubrica/${id}`)

/**
 * Una voce da proporre nel compositore: dalla rubrica (con gli invii), fra i colleghi, o un gruppo della
 * rubrica (539), che scelto mette tutti i suoi indirizzi; il gruppo non ha indirizzo e si cerca per nome.
 */
export type Suggerimento = {
  indirizzo: string; nome: string | null; invii: number; collega: boolean
  gruppo?: { id: string; voci: string[] }
}

/** I destinatari di un gruppo, come si scrivono nel campo: «Nome <indirizzo>» o l'indirizzo. */
export function vociDelGruppo(r: Rubrica, g: GruppoRubrica): string[] {
  const perId = new Map(r.contatti.map((c) => [c.id, c]))
  return g.contatti.flatMap((id) => { const c = perId.get(id); return c ? [voceDestinatario(c)] : [] })
}

/** Gruppi, rubrica e colleghi in un elenco solo: prima i gruppi, poi chi riceve piu' spesso. */
export function suggerimentiDa(r: Rubrica): Suggerimento[] {
  return [
    ...(r.gruppi ?? []).map((g) => ({
      indirizzo: '', nome: g.nome, invii: 0, collega: false, gruppo: { id: g.id, voci: vociDelGruppo(r, g) },
    })).filter((g) => g.gruppo.voci.length > 0),
    ...r.contatti.map((c) => ({ indirizzo: c.indirizzo, nome: c.nome, invii: c.invii, collega: false })),
    ...r.colleghi.map((c) => ({ indirizzo: c.indirizzo, nome: c.nome, invii: 0, collega: true })),
  ]
}

/**
 * Il nome di un gruppo scritto per intero in A, Cc o Ccn diventa i suoi indirizzi (539). Si confronta senza
 * maiuscole; quello che non e' un gruppo resta com'e'. Restituisce il testo nuovo, o null se non cambia.
 */
export function espandiGruppi(testo: string, voci: Suggerimento[]): string | null {
  const gruppi = new Map(voci.filter((v) => v.gruppo).map((v) => [v.nome!.trim().toLowerCase(), v.gruppo!.voci]))
  if (!gruppi.size) return null
  let cambiato = false
  const parti = testo.split(/[,;]/).map((p) => p.trim()).filter(Boolean).flatMap((p) => {
    const membri = p.includes('@') ? undefined : gruppi.get(p.replace(/\s+/g, ' ').toLowerCase())
    if (!membri) return [p]
    cambiato = true
    return membri
  })
  if (!cambiato) return null
  const visti = new Set<string>()
  return parti.filter((p) => {
    const chiave = soloIndirizzo(p).toLowerCase()
    if (visti.has(chiave)) return false
    visti.add(chiave)
    return true
  }).join(', ')
}

/**
 * Il destinatario da scrivere nel campo: «Nome <indirizzo>», o il solo indirizzo se il nome contiene
 * una virgola o un punto e virgola, che nel campo separano i destinatari.
 */
export function voceDestinatario(v: { nome: string | null; indirizzo: string }): string {
  return v.nome && !/[,;<>"]/.test(v.nome) ? `${v.nome} <${v.indirizzo}>` : v.indirizzo
}

/** Le voci che cominciano (nel nome o nell'indirizzo, anche dopo un punto o uno spazio) con quello che si scrive. */
export function filtraSuggerimenti(voci: Suggerimento[], scritto: string, massimo = 8): Suggerimento[] {
  const q = scritto.trim().toLowerCase()
  if (!q) return []
  const comincia = (t: string | null) => !!t && (t.toLowerCase().startsWith(q)
    || t.toLowerCase().split(/[\s.@_-]+/).some((p) => p.startsWith(q)))
  return voci.filter((v) => comincia(v.indirizzo) || comincia(v.nome)).slice(0, massimo)
}

export const elencaCartelle = (casellaId: string) =>
  api.get<Cartella[]>(`/posta/caselle/${casellaId}/cartelle`)

const LIMITE_THREAD = 30

function queryThread(filtri: FiltriThread, conTesto: boolean): string {
  const q = new URLSearchParams()
  if (filtri.cartella) q.set('cartella', filtri.cartella)
  if (conTesto && filtri.cerca) q.set('cerca', filtri.cerca)
  if (filtri.stato) q.set('stato', filtri.stato)
  if (filtri.persona_id) q.set('persona_id', filtri.persona_id)
  if (filtri.non_letti) q.set('non_letti', '1')
  if (filtri.bandierina) q.set('bandierina', filtri.bandierina)
  if (filtri.etichetta) q.set('etichetta', filtri.etichetta)
  if (filtri.ordine && filtri.ordine !== 'data') q.set('ordine', filtri.ordine)
  if (filtri.verso === 'asc') q.set('verso', 'asc')
  if (filtri.prima_di) q.set('prima_di', filtri.prima_di)
  q.set('limite', String(filtri.limite ?? LIMITE_THREAD))
  return q.toString()
}

/**
 * Elenco della vista «Tutte le caselle»: la casella propria e quelle di gruppo in una sola lista,
 * con lo stesso cursore. Il testo cercato viaggia nel corpo, per la ragione detta sotto; la rotta
 * e' nata insieme alla variante POST, quindi qui non serve la ricaduta sulla GET.
 */
export function elencaThreadTutte(filtri: FiltriThread, segnale?: AbortSignal) {
  const cerca = filtri.cerca?.trim() ?? ''
  if (!cerca) {
    return api.get<PaginaThread>(`/posta/tutte/thread?${queryThread({ ...filtri, cartella: null }, false)}`,
      { segnale })
  }
  return api.post<PaginaThread>('/posta/tutte/thread/cerca', {
    cerca,
    ambito: filtri.ambito,
    stato: filtri.stato,
    persona_id: filtri.persona_id,
    non_letti: filtri.non_letti ?? false,
    bandierina: filtri.bandierina ?? undefined,
    etichetta: filtri.etichetta ?? undefined,
    prima_di: filtri.prima_di ?? null,
    limite: filtri.limite ?? LIMITE_THREAD,
  }, { segnale })
}

export const leggiVista = () => api.get<VistaPosta>('/posta/vista')

export const leggiAvviso = () => api.get<StatoAvviso>('/posta/avviso-in-calce')

/** La richiesta di modifica in corso o decisa da poco; null se non ce n'e'. */
export const leggiRichiestaAvviso = () => api.get<RichiestaAvviso | null>('/posta/avviso-in-calce/richiesta')

/** Chiede di cambiare il testo: vale solo dopo l'approvazione del Referente privacy o del DPO. */
export const chiediModificaAvviso = (testo: string, motivazione: string) =>
  api.post<{ id: string }>('/posta/avviso-in-calce/richieste', { testo, motivazione })

export const salvaVista = (vista: VistaPosta) => api.put<VistaPosta>('/posta/vista', vista)

/** «Aggiorna ora»: l'API accoda la lettura della casella; i messaggi arrivano in qualche minuto. */
export const aggiornaOra = (casellaId: string) =>
  api.post<{ accodata: boolean }>(`/posta/caselle/${casellaId}/sincronizza`)

/**
 * Vero quando la rotta POST di ricerca risponde, falso quando non e' registrata, nullo
 * finche' non si sa. Il valore resta per la durata della pagina: la verifica costa una
 * richiesta e non va ripetuta a ogni tasto.
 */
let ricercaConPost: boolean | null = null

/**
 * Elenco dei thread. Senza testo di ricerca si usa la GET con i filtri in query string:
 * sono identificativi e codici, non dati digitati.
 *
 * Con un testo di ricerca la query string non e' utilizzabile: Fastify registra req.url
 * completo nel log del container, quindi un cognome o un indirizzo resterebbe in chiaro
 * fuori dalla cifratura a riposo e dalla conservazione dichiarata. Il testo viaggia nel corpo
 * di POST /posta/caselle/:id/thread/cerca.
 *
 * Finche' quella rotta non e' registrata ovunque serve una ricaduta sulla GET. Il 404 di una
 * rotta assente ha lo stesso corpo del 404 di una casella fuori perimetro, quindi prima di
 * ricadere si chiede una riga sola senza testo: se anche quella risponde 404 l'errore e' di
 * perimetro e si propaga, e il testo digitato non finisce nella query string.
 */
export async function elencaThread(casellaId: string, filtri: FiltriThread, segnale?: AbortSignal) {
  const cerca = filtri.cerca?.trim() ?? ''
  if (!cerca) {
    return api.get<PaginaThread>(
      `/posta/caselle/${casellaId}/thread?${queryThread(filtri, false)}`, { segnale })
  }

  if (ricercaConPost !== false) {
    try {
      const pagina = await api.post<PaginaThread>(
        `/posta/caselle/${casellaId}/thread/cerca`,
        {
          cartella: filtri.cartella ?? null,
          cerca,
          ambito: filtri.ambito,
          stato: filtri.stato,
          persona_id: filtri.persona_id,
          non_letti: filtri.non_letti ?? false,
          bandierina: filtri.bandierina ?? undefined,
          etichetta: filtri.etichetta ?? undefined,
          prima_di: filtri.prima_di ?? null,
          limite: filtri.limite ?? LIMITE_THREAD,
        },
        { segnale },
      )
      ricercaConPost = true
      return pagina
    } catch (errore) {
      if (!(errore instanceof ErroreApi) || errore.stato !== 404) throw errore
      await api.get<PaginaThread>(`/posta/caselle/${casellaId}/thread?limite=1`, { segnale })
      ricercaConPost = false
    }
  }

  return api.get<PaginaThread>(
    `/posta/caselle/${casellaId}/thread?${queryThread(filtri, true)}`, { segnale })
}

export const apriThread = (threadId: string) => api.get<ThreadAperto>(`/posta/thread/${threadId}`)

/** La rotta del .eml di un messaggio della webmail (2/11/2026). */
export const percorsoEml = (postaId: string) => `/posta/messaggi/${postaId}/eml`

/** La rotta del .eml di un messaggio dell'archivio delle caselle. */
export const percorsoEmlArchiviato = (casellaId: string, postaId: string) =>
  `/posta/archivio/${casellaId}/messaggi/${postaId}/eml`

/**
 * Scarica un file dell'API con il nome che dichiara (Content-Disposition, filename* in UTF-8). La GET passa
 * anche impersonando. Gli errori hanno la forma e le parole di `api` (ErroreApi).
 */
export async function scaricaFile(percorso: string): Promise<void> {
  const risposta = await fetch(`/api${percorso}`, { credentials: 'include' })
  if (!risposta.ok) {
    const corpo = await risposta.json().catch(() => null) as { errore?: string } | null
    throw new ErroreApi(risposta.status === 404
      ? 'Non c’è, oppure non rientra fra quello che puoi vedere con i tuoi incarichi.'
      : corpo?.errore ?? `Il server non ha risposto come doveva (errore ${risposta.status}). Riprova fra poco.`,
    risposta.status, corpo)
  }
  const disposizione = risposta.headers.get('content-disposition') ?? ''
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposizione)
  const semplice = /filename="([^"]+)"/i.exec(disposizione)
  const nome = utf8 ? decodeURIComponent(utf8[1]!) : semplice?.[1] ?? 'messaggio.eml'
  const indirizzo = URL.createObjectURL(await risposta.blob())
  const a = document.createElement('a')
  a.href = indirizzo
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(indirizzo), 1000)
}

export const segnaLetto = (threadId: string, letto: boolean) =>
  api.post<{ letto: boolean }>(`/posta/thread/${threadId}/letto`, { letto })

export const cambiaStato = (threadId: string, stato: StatoThread) =>
  api.post<{ stato: StatoThread }>(`/posta/thread/${threadId}/stato`, { stato })

/** «È indesiderata» e «Non è indesiderata» (542): quale comando proporre, null se nessuno. */
export type ComandoIndesiderata = 'segna' | 'togli'

export const comandoIndesiderata = (threadId: string) =>
  api.get<{ comando: ComandoIndesiderata | null }>(`/posta/thread/${threadId}/indesiderata`)

/** Sposta i messaggi ricevuti della conversazione e li insegna all'antispam (spam o non spam). */
export const segnaIndesiderata = (threadId: string, indesiderata: boolean) =>
  api.post<{ spostati: number; antispam: number }>(`/posta/thread/${threadId}/indesiderata`, { indesiderata })

/**
 * La voce «Cestino» (535): quante conversazioni ci sono e che cosa succede loro. Lo svuotamento passa
 * dalla regola di cancellazione (500): a regola spenta il cestino non si svuota da solo.
 */
export type RiepilogoCestino = {
  conversazioni: number
  giorni: number
  regola: { attiva: boolean; preavviso_giorni: number; comprende_schede: boolean }
}

/** Il cestino di una casella, o di tutte quelle della vista unica con `null`. */
export const riepilogoCestino = (casellaId: string | null, automatica = false) =>
  api.get<RiepilogoCestino>(casellaId ? `/posta/caselle/${casellaId}/cestino` : '/posta/tutte/cestino',
    { automatica })

/** Che cosa succede alle conversazioni nel cestino, in una frase per la vista «Cestino». */
export function testoCestino(r: RiepilogoCestino): string {
  if (!r.regola.attiva) {
    return 'Il cestino non si svuota da solo finché la regola di cancellazione della posta è spenta: '
      + 'le conversazioni restano qui finché non le ripristini.'
  }
  return `Le conversazioni nel cestino si cancellano dopo ${r.giorni} giorni, dall’ingresso nel cestino o dall’ultimo `
    + 'messaggio se più recente, con la regola di cancellazione della posta: prima passano da «In cancellazione», '
    + `con almeno ${r.regola.preavviso_giorni} giorni di preavviso. Fino ad allora «Ripristina» le riporta fra le aperte.`
    + (r.regola.comprende_schede ? '' : ' Quelle collegate a una scheda non si cancellano.')
}

/** Lo stato della conversazione detto come lo dice la pagina. */
export function etichettaStato(stato: StatoThread): string | null {
  return stato === 'archiviato' ? 'archiviata' : stato === 'cestinato' ? 'nel cestino' : null
}

export const agganciaThread = (threadId: string, a: { persona_id?: string; intervento_id?: string }) =>
  api.post(`/posta/thread/${threadId}/aggancia`, a)

export const inviaMessaggio = (messaggio: MessaggioDaInviare) =>
  api.post<{ id: string; thread_id?: string; stato_invio: string }>('/posta/messaggi', messaggio)

export const prenotaAllegato = (nome: string, mime: string, dimensione: number) =>
  api.post<{ storage_key: string; url_caricamento: string }>(
    '/posta/allegati/prenota', { nome, mime, dimensione })

/** Verdetto dell'antivirus su un allegato prima dell'invio (566). */
export type EsitoControllo = 'in_attesa' | 'pulito' | 'infetto' | 'non_scansionato'

/**
 * Controllo degli allegati prima dell'invio (566): avvia quelli ancora da controllare e dice a che punto sono.
 * `antivirus_fermo`: l'antivirus non risponde (episodio aperto della guardia, o il motore muto adesso).
 */
export const controllaAllegati = (allegati: AllegatoDaInviare[]) =>
  api.post<{ allegati: { storage_key: string; esito: EsitoControllo }[]; antivirus_fermo: boolean }>(
    '/posta/allegati/controlla', { allegati })

/** `nonControllato`: scaricamento prima del controllo antivirus, dopo l'avviso (526); finisce nel registro. */
export const urlAllegato = (allegatoId: string, nonControllato = false) =>
  api.get<{ url: string }>(`/posta/allegati/${allegatoId}/url${nonControllato ? '?non_controllato=1' : ''}`)

export const elencaSupervisioni = () => api.get<Supervisione[]>('/posta/supervisioni')

export const revocaSupervisione = (id: string) => api.elimina(`/posta/supervisioni/${id}`)

/** Le supervisioni in corso su tutte le caselle: per chi ha `posta.supervisione_concedi`. */
export const elencaSupervisioniInCorso = () => api.get<SupervisioneInCorso[]>('/posta/supervisioni/tutte')

/** Le caselle di cui chiedere la lettura e la durata massima (`posta.supervisione_giorni`). */
export const caselleSorvegliabili = () =>
  api.get<{ massimo_giorni: number; caselle: CasellaSorvegliabile[] }>('/posta/supervisioni/caselle')

export const elencaRichiesteSupervisione = () =>
  api.get<RichiestaSupervisione[]>('/posta/supervisioni/richieste')

/** Chiede l'approvazione `supervisione_posta`: la decide il Referente privacy (o il DPO) in «Approvazioni». */
export const chiediSupervisione = (dati: { casella_id: string; motivazione: string; giorni: number }) =>
  api.post<{ id: string }>('/posta/supervisioni/richieste', dati)

/** Con l'approvazione concessa, apre la lettura per la durata approvata. */
export const apriSupervisione = (dati: {
  casella_id: string; approvazione_id: string; motivazione: string; giorni: number
}) => api.post<{ id: string; scade_il: string }>('/posta/supervisioni', dati)

// --- regola di cancellazione e conservazione (500) ---------------------------------------------

/** La regola: spenta per difetto; accesa, cancella le conversazioni oltre `giorni` dall'ultimo messaggio. */
export type RegolaCancellazione = {
  attiva: boolean
  giorni: number
  preavviso_giorni: number
  comprende_schede: boolean
}

export type StatoRegola = {
  regola: RegolaCancellazione & { in_vigore_dal: string | null }
  limiti: { giorni: [number, number]; preavviso_giorni: [number, number] }
  /** L'ultima richiesta di cambio, aperta o decisa negli ultimi trenta giorni. */
  richiesta: {
    id: string
    stato: 'in_attesa' | 'approvata' | 'respinta' | 'scaduta' | 'annullata'
    regola: RegolaCancellazione | null
    prima: RegolaCancellazione | null
    richiesta_il: string
    scade_il: string
    decisa_il: string | null
    nota_decisione: string | null
    richiedente: string
    mia: boolean
  } | null
  puo_chiedere: boolean
  /** Ruoli che decidono e che oggi nessuno porta: la richiesta non si aprirebbe. */
  decisori_mancanti: string[]
  conteggi: { in_cancellazione: number; in_conservazione: number }
}

export type VoceInCancellazione = VoceThread & {
  casella: string; entrata_il: string; prevista_il: string
  /** Nel cestino (535): ci entra per i suoi 30 giorni, non per il termine della regola. */
  dal_cestino: boolean
}

export type VoceInConservazione = VoceThread & {
  casella: string
  conservata_il: string
  conservata_da: UtenteBreve | null
  motivazione: string
}

export type PaginaSezione<T> = { totale: number; conversazioni: T[]; prossimo: string | null }

export const leggiRegola = () => api.get<StatoRegola>('/posta/conservazione/regola')

/** Chiede l'approvazione `regola_conservazione_posta`: la decide il Referente privacy (o il DPO) in «Approvazioni». */
export const chiediRegola = (dati: RegolaCancellazione & { motivazione: string }) =>
  api.post<{ id: string }>('/posta/conservazione/regola/richieste', dati)

export const ritiraRichiestaRegola = (id: string) =>
  api.post<{ stato: string }>(`/posta/conservazione/regola/richieste/${id}/ritira`)

const paginaSezione = (dopo: string | null) =>
  dopo ? `?dopo=${encodeURIComponent(dopo)}` : ''

export const elencaInCancellazione = (dopo: string | null = null) =>
  api.get<PaginaSezione<VoceInCancellazione> & { regola: RegolaCancellazione }>(
    `/posta/conservazione/in-cancellazione${paginaSezione(dopo)}`)

export const elencaInConservazione = (dopo: string | null = null) =>
  api.get<PaginaSezione<VoceInConservazione>>(`/posta/conservazione/in-conservazione${paginaSezione(dopo)}`)

export const mettiInConservazione = (threadId: string, motivazione: string) =>
  api.post<{ conservata: true }>(`/posta/thread/${threadId}/conservazione`, { motivazione })

export const togliDallaConservazione = (threadId: string) =>
  api.elimina<{ conservata: false; prevista_il: string | null }>(`/posta/thread/${threadId}/conservazione`)

/** La regola in una frase, per la sezione e per «Approvazioni». */
export function regolaATesto(r: RegolaCancellazione): string {
  if (!r.attiva) return 'spenta: nessuna conversazione si cancella'
  return `si cancellano le conversazioni il cui ultimo messaggio ha più di ${r.giorni} giorni, `
    + `con ${r.preavviso_giorni} giorni di preavviso; le conversazioni collegate a una scheda `
    + (r.comprende_schede ? 'sono comprese' : 'restano fuori')
}

// --- caselle di gruppo ------------------------------------------------------------------------

/** Una persona che usa la casella di gruppo, con il livello (504). */
export type MembroGruppo = {
  id: string; nome: string; cognome: string; email: string; stato: string; livello: LivelloCasella
  aggiunto_il: string
}

/**
 * Casella di gruppo come la vede chi la gestisce (`utente.gestisci`). Le password non arrivano
 * mai: al loro posto gli asterischi, se ci sono.
 */
export type CasellaGruppo = {
  id: string; ambito: 'gruppo'; nome_visualizzato: string; indirizzo: string
  smtp_host: string; smtp_porta: number; smtp_sicurezza: string; smtp_utente: string
  smtp_password: string | null
  imap_host: string | null; imap_porta: number | null; imap_sicurezza: string | null
  imap_utente: string | null; imap_password: string | null; imap_cartelle: string[]
  imap_ultima_sincro: string | null
  attiva: boolean; ultima_prova: string | null; ultimo_errore: string | null
  membri: MembroGruppo[]
  /** 'ospitata' (502): niente server ne' password, la posta arriva dalla VM di ricezione. */
  modalita: Modalita
  ultimo_arrivo_il: string | null
  /** Attiva e senza membri in uso (504): da associare a persone, a un titolare o a un progetto. */
  da_associare: boolean
  /** Firma personalizzata (508); null: la firma di chi scrive. */
  firma: string | null
  /** Nell'archivio delle caselle (507). */
  archiviata: boolean
}

export const elencaGruppi = () => api.get<CasellaGruppo[]>('/posta/gruppi')

/** Utenze in uso fra cui scegliere chi usa una casella di gruppo. */
export type UtenteInUso = { id: string; nome: string; cognome: string; email: string }

export const candidatiGruppo = () => api.get<UtenteInUso[]>('/posta/gruppi/candidati')

/** «E' la casella personale di…»: la casella di gruppo senza membri diventa personale (504). */
export const rendiPersonale = (casellaId: string, utenteId: string) =>
  api.post<{ ambito: 'utente' }>(`/posta/caselle/${casellaId}/personale`, { utente_id: utenteId })

// --- caselle di progetto (504) ----------------------------------------------------------------

/** Una casella di progetto in Posta → «Caselle condivise». */
export type CasellaProgettoAmministrata = {
  id: string; indirizzo: string; nome_visualizzato: string; attiva: boolean; modalita: Modalita
  ultimo_arrivo_il: string | null
  progetto: { id: string; titolo: string; codice_cidis: string | null }
  persone: number; messaggi: number
}

export type ProgettoSenzaCasella = { id: string; codice_cidis: string | null; titolo: string }

export const elencaCaselleProgetto = () => api.get<CasellaProgettoAmministrata[]>('/posta/progetti')

export const progettiSenzaCasella = (cerca = '') =>
  api.get<ProgettoSenzaCasella[]>(`/posta/progetti/senza-casella${cerca ? `?cerca=${encodeURIComponent(cerca)}` : ''}`)

/** «Associa a un progetto»: la casella di gruppo diventa la casella del progetto, con il suo indirizzo. */
export const associaAProgetto = (casellaId: string, progettoId: string) =>
  api.post<{ ambito: 'progetto' }>(`/posta/caselle/${casellaId}/progetto`, { progetto_id: progettoId })

/** La sezione «Posta del progetto», come la restituisce GET /progetti/:id/casella. */
export type PostaDelProgetto = {
  casella: {
    id: string; indirizzo: string; nome_visualizzato: string; attiva: boolean; modalita: Modalita
    ultimo_arrivo_il: string | null; creata_il: string; messaggi: number | null
    /** Firma personalizzata (508); null: la firma di chi scrive. */
    firma: string | null
  } | null
  dominio: string
  prefissi: PrefissoProgetto[]
  parte: { minima: number; massima: number }
  nome_proposto: string
  webmail_attiva: boolean
  ricezione_attiva: boolean
  livello: LivelloCasella | null
  persone: PersonaCasellaProgetto[] | null
  non_letti: number | null
  puo: { creare: boolean; configurare: boolean; decidere: boolean; aprire: boolean; collegare_esistente: boolean }
}

export type PrefissoProgetto = 'progetto' | 'pg'

export type PersonaCasellaProgetto = {
  id: string; nome: string; cognome: string; stato: string
  livello: LivelloCasella | null
  diritto: 'referente' | 'project_manager' | null
  livello_membro: LivelloCasella | null
  aggiunto_il: string | null
}

/** La casella nuova: come la manda il modulo del progetto e la sezione della pagina. */
export type NuovaCasellaProgetto = {
  prefisso?: PrefissoProgetto; parte?: string; indirizzo_esistente?: string
  nome_visualizzato?: string
  persone: { utente_id: string; livello: LivelloCasella }[]
}

export const PREFISSI_PROGETTO: PrefissoProgetto[] = ['progetto', 'pg']
export const DOMINIO_PROGETTI = 'cidisonlus.org'

/** La parte dopo il trattino: le stesse regole dell'API (servizi/caselle-progetto.ts). */
export function parteValida(parte: string): string | null {
  const p = parte.trim()
  if (p.length < 2 || p.length > 40) return 'Da 2 a 40 caratteri.'
  if (!/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/.test(p)) {
    return 'Lettere minuscole senza accenti, cifre, punto e trattino; niente punto o trattino all’inizio, alla fine o due di fila.'
  }
  return null
}

export const ETICHETTE_LIVELLO: Record<LivelloCasella, string> = { legge: 'legge', scrive: 'legge e scrive' }

export const leggiPostaDelProgetto = (progettoId: string) =>
  api.get<PostaDelProgetto>(`/progetti/${progettoId}/casella`)

export const creaCasellaProgetto = (progettoId: string, dati: NuovaCasellaProgetto) =>
  api.post<{ id: string; indirizzo: string }>(`/progetti/${progettoId}/casella`, dati)

export const modificaCasellaProgetto = (progettoId: string,
  dati: { attiva?: boolean; nome_visualizzato?: string; prefisso?: PrefissoProgetto; parte?: string }) =>
  api.patch<{ modificata: boolean }>(`/progetti/${progettoId}/casella`, dati)

export const impostaPersonaProgetto = (progettoId: string, utenteId: string, livello: LivelloCasella) =>
  api.put<{ esito: 'aggiunta' | 'livello' | 'invariata' }>(
    `/progetti/${progettoId}/casella/persone/${utenteId}`, { livello })

export const togliPersonaProgetto = (progettoId: string, utenteId: string) =>
  api.elimina(`/progetti/${progettoId}/casella/persone/${utenteId}`)

export const candidatiCasellaProgetto = (progettoId: string) =>
  api.get<UtenteInUso[]>(`/progetti/${progettoId}/casella/candidati`)

// --- posta ospitata (502) ---------------------------------------------------------------------

/**
 * Modalita' di una casella: 'imap' sta su un server di posta che il gestionale legge; 'ospitata' non
 * ha server, la posta arriva dalla VM di posta e parte da li' (da Scaleway TEM le sole email
 * automatiche della casella dell'ente).
 */
export type Modalita = 'imap' | 'ospitata'

/** Una casella in Posta → «Caselle e ricezione», come la vede chi gestisce utenti o posta. */
export type CasellaAmministrata = {
  id: string
  ambito: AmbitoCasella
  modalita: Modalita
  indirizzo: string
  nome_visualizzato: string
  attiva: boolean
  ultimo_arrivo_il: string | null
  ospitata_il: string | null
  imap_ultima_sincro: string | null
  lettura_imap: boolean
  ultimo_errore: string | null
  titolare: { id: string; nome: string; cognome: string; email: string; stato: string } | null
  /** Casella di progetto (504): il progetto. */
  progetto: { id: string; titolo: string; codice_cidis: string | null } | null
  membri: number
  messaggi: number
  da_associare: boolean
  /** Dismessa (506): persona che non lavora piu' con CIDIS, o indirizzo non piu' in uso. */
  dismessa_motivo: MotivoDismissione | null
  /** Nell'archivio delle caselle (507). */
  archiviata: boolean
  /** Inoltro in deroga attivo (536): la posta va anche fuori CIDIS. */
  inoltro: { id: string; destinazione: string } | null
}

/** Stato della ricezione: ultima chiamata della VM, ultimo messaggio, arrivi fermi a webmail spenta. */
export type StatoRicezione = {
  ultima_chiamata_il: string | null
  ultimo_arrivo_il: string | null
  in_attesa_dal: string | null
}

export type UtenteSenzaCasella = { id: string; nome: string; cognome: string; email: string }

export const elencaCaselleAmministrate = () =>
  api.get<{ caselle: CasellaAmministrata[]; ricezione: StatoRicezione }>('/posta/amministrazione/caselle')

export const utentiSenzaCasella = () => api.get<UtenteSenzaCasella[]>('/posta/amministrazione/senza-casella')

/** Inoltro in deroga attivo (536), con chi l'ha concesso e il motivo. */
export type InoltroInDeroga = {
  id: string
  casella_id: string
  casella: string
  nome_casella: string
  titolare: { id: string; nome: string; cognome: string } | null
  destinazione: string
  motivo: string
  concessa_il: string
  concessa_da: { id: string; nome: string; cognome: string }
  /** Avvisi della posta interna (537): partiti, email contate, ultimo, in attesa. */
  avvisi_inviati: number
  email_avvisate: number
  ultimo_avviso_il: string | null
  avvisi_in_attesa: number
}

/** Gli inoltri attivi: chi gestisce le utenze e la Referente privacy (`posta.inoltri_controlla`). */
export const elencaInoltri = () => api.get<InoltroInDeroga[]>('/posta/inoltri')

export const concediInoltro = (casellaId: string, destinazione: string, motivo: string) =>
  api.post<InoltroInDeroga>(`/posta/amministrazione/caselle/${casellaId}/inoltro`, { destinazione, motivo })

export const revocaInoltro = (id: string) => api.post<{ revocato: boolean }>(`/posta/inoltri/${id}/revoca`)

/**
 * Credenziale di un programma di posta (525, 547): una per persona sulla stessa casella, con il nome utente
 * uguale all'indirizzo della casella. `ultimo_uso_il` e' l'ultimo messaggio spedito dal programma.
 */
export type CredenzialeProgramma = {
  id: string
  casella: { id: string; indirizzo: string; nome_visualizzato: string }
  persona: { id: string; nome: string; cognome: string; email: string }
  creata_il: string
  creata_da: { id: string; nome: string; cognome: string } | null
  ultimo_uso_il: string | null
}

/** Le credenziali attive, di tutte le caselle o di una: chi gestisce le utenze. */
export const elencaCredenziali = (casellaId?: string) =>
  api.get<CredenzialeProgramma[]>(`/posta/credenziali${casellaId ? `?casella=${encodeURIComponent(casellaId)}` : ''}`)

export const revocaCredenziale = (id: string) => api.post<{ revocata: boolean }>(`/posta/credenziali/${id}/revoca`)

export const creaPersonaliOspitate = (utenti: string[]) =>
  api.post<{ create: number; saltate: number; indirizzi_in_uso: number }>('/posta/amministrazione/ospitate', { utenti })

export const convertiInOspitata = (casellaId: string) =>
  api.post<{ modalita: Modalita }>(`/posta/caselle/${casellaId}/ospita`)

/**
 * Casella ospitata prima del passaggio dell'MX (504): la ricezione del gestionale non ha mai
 * consegnato niente, e la casella non riceve ancora. La frase la dicono la pagina Posta e la pagina del
 * progetto.
 */
export const RICEZIONE_NON_ATTIVA = 'La ricezione nel gestionale non è ancora attiva: questa casella riceverà '
  + 'i messaggi dal giorno in cui la posta di CIDIS passerà al gestionale. Fino ad allora i messaggi per '
  + 'questo indirizzo non arrivano qui.'

/** L'indicatore «Ricezione» di una casella ospitata: «ultimo messaggio oggi alle 10:15». */
export const ultimoMessaggio = (iso: string | null) =>
  (iso ? `ultimo messaggio ${quandoBreve(iso)}` : 'nessun messaggio ricevuto finora')

/** «oggi alle 10:15», «ieri alle 18:02», altrimenti la data: per gli indicatori della ricezione. */
export function quandoBreve(iso: string | null): string {
  if (!iso) return 'mai'
  const d = new Date(iso)
  const ora = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
  const oggi = new Date()
  const ieri = new Date(oggi.getTime() - 86_400_000)
  if (d.toDateString() === oggi.toDateString()) return `oggi alle ${ora}`
  if (d.toDateString() === ieri.toDateString()) return `ieri alle ${ora}`
  return `${d.toLocaleDateString('it-IT')} alle ${ora}`
}

/** Aggiunge una persona a una casella di gruppo, o ne cambia il livello (504; predefinito «scrive»). */
export const aggiungiMembro = (casellaId: string, utenteId: string, livello: LivelloCasella = 'scrive') =>
  api.post<{ aggiunto: boolean; esito: string }>(`/posta/gruppi/${casellaId}/membri`, { utente_id: utenteId, livello })

export const togliMembro = (casellaId: string, utenteId: string) =>
  api.elimina(`/posta/gruppi/${casellaId}/membri/${utenteId}`)

// --- firme (508) ------------------------------------------------------------------------------

/** La firma di una casella da cui si scrive, come la compone l'API (servizi/firme.ts). */
export type FirmaCasella = {
  casella_id: string
  ambito: 'utente' | 'gruppo' | 'progetto'
  /** La casella ha una firma propria, con il nome di chi scrive al posto di {nome}. */
  personalizzata: boolean
  con_nome: string
  /** «Senza il mio nome»: uguale a `con_nome` sulla casella personale. */
  senza_nome: string
}

export type FirmeDellaPersona = {
  persona: { con_nome: string; senza_nome: string }
  caselle: FirmaCasella[]
}

export const elencaFirme = () => api.get<FirmeDellaPersona>('/posta/firme')

/** La firma da inserire per la casella; «Senza il mio nome» vale solo sulle condivise. '' se non c'e'. */
export function firmaPer(firme: FirmeDellaPersona | null, casellaId: string, senzaNome = false): string {
  const f = firme?.caselle.find((c) => c.casella_id === casellaId)
  if (!f) return ''
  return senzaNome && f.ambito !== 'utente' ? f.senza_nome : f.con_nome
}

/**
 * Tutte le firme che il compositore puo' avere inserito per chi scrive, dalla piu' lunga: servono a
 * ritrovarla nel testo quando si cambia «Da» o si sceglie «Senza il mio nome». Una firma modificata a
 * mano, o scritta da un altro nella bozza condivisa, non e' fra queste e non si tocca.
 */
export function firmeNote(firme: FirmeDellaPersona | null): string[] {
  if (!firme) return []
  const tutte = [firme.persona.con_nome, firme.persona.senza_nome,
    ...firme.caselle.flatMap((c) => [c.con_nome, c.senza_nome])]
  return [...new Set(tutte.filter(Boolean))].sort((a, b) => b.length - a.length)
}

/** Il testo con cui si apre il compositore: due righe vuote dove scrivere, la firma, poi il resto. */
export const testoConFirma = (firma: string, seguito = '') => (firma ? `\n\n${firma}${seguito}` : seguito)

export type Profilo = {
  nome: string; cognome: string; email: string
  sede: { nome: string; indirizzo: string | null; comune: string | null } | null
  cellulare: string | null; telefono_ufficio: string | null
  /** Anteprima della firma della persona. */
  firma: string
  usa_posta: boolean
}

export const leggiProfilo = () => api.get<Profilo>('/profilo')

export const salvaTelefoni = (cellulare: string | null, telefonoUfficio: string | null) =>
  api.put<{ cellulare: string | null; telefono_ufficio: string | null }>('/profilo/telefoni',
    { cellulare, telefono_ufficio: telefonoUfficio })

/** Firma personalizzata di una casella condivisa; null la toglie. */
export const salvaFirmaCasella = (casellaId: string, firma: string | null) =>
  api.put<{ firma: string | null; modificata: boolean }>(`/posta/caselle/${casellaId}/firma`, { firma })

/** Il segnaposto del nome di chi scrive nella firma di una casella condivisa. */
export const SEGNAPOSTO_NOME = '{nome}'

/** Vero quando l'API ha rifiutato perche' la funzione 'webmail_completa' e' spenta. */
export function interruttoreSpento(errore: unknown): boolean {
  return errore instanceof ErroreApi && errore.stato === 409 && errore.codice === 'INTERRUTTORE_SPENTO'
}

/** Vero quando manca l'approvazione a quattro occhi che la supervisione pretende. */
export function approvazioneRichiesta(errore: unknown): boolean {
  return errore instanceof ErroreApi && errore.stato === 428 && errore.codice === 'APPROVAZIONE_RICHIESTA'
}

// --- indirizzi e date ------------------------------------------------------------------------

/** Da "Mario Rossi <m.rossi@example.org>" ricava "Mario Rossi"; altrimenti la parte locale. */
export function nomeBreve(indirizzo: string): string {
  const conNome = /^\s*"?([^"<]+?)"?\s*<[^>]+>\s*$/.exec(indirizzo)
  if (conNome) return conNome[1]!.trim()
  const solo = soloIndirizzo(indirizzo)
  const chiocciola = solo.indexOf('@')
  return chiocciola > 0 ? solo.slice(0, chiocciola) : solo
}

/** «Nome <indirizzo>» se c'e' il nome, altrimenti l'indirizzo: per chi scrive e per i destinatari, completi. */
export function nomeEIndirizzo(voce: string): string {
  const conNome = /^\s*"?([^"<]+?)"?\s*<([^>]+)>\s*$/.exec(voce)
  if (conNome) return `${conNome[1]!.trim()} <${conNome[2]!.trim()}>`
  return soloIndirizzo(voce)
}

/** La sola parte fra parentesi angolari, quando c'e'. */
export function soloIndirizzo(indirizzo: string): string {
  const dentro = /<([^>]+)>/.exec(indirizzo)
  return (dentro ? dentro[1]! : indirizzo).trim()
}

const oreMinuti = new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' })
const giornoMese = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short' })
const dataCorta = new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' })
const dataCompleta = new Intl.DateTimeFormat('it-IT', { dateStyle: 'medium', timeStyle: 'short' })

/** Nell'elenco la data sta su poche lettere: oggi l'ora, quest'anno il giorno, prima l'anno. */
export function dataElenco(iso: string): string {
  const quando = new Date(iso)
  const adesso = new Date()
  if (quando.toDateString() === adesso.toDateString()) return oreMinuti.format(quando)
  if (quando.getFullYear() === adesso.getFullYear()) return giornoMese.format(quando)
  return dataCorta.format(quando)
}

export const dataEstesa = (iso: string) => dataCompleta.format(new Date(iso))

export function peso(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

// --- corpo HTML dei messaggi ------------------------------------------------------------------

/**
 * Elementi che non hanno ragione di esistere dentro un messaggio e che portano codice o
 * richieste di rete: si rimuovono con il loro contenuto. <style> rientra nell'elenco perche'
 * @import e url() caricano risorse remote quanto un <img>.
 *
 * Il confronto avviene su localName in maiuscolo, non su tagName: fuori dal namespace HTML
 * tagName conserva il minuscolo ('svg', 'math') e il confronto fallirebbe.
 */
const TAG_RIMOSSI = new Set([
  'SCRIPT', 'IFRAME', 'OBJECT', 'EMBED', 'APPLET', 'FRAME', 'FRAMESET', 'NOSCRIPT',
  'LINK', 'META', 'BASE', 'STYLE', 'FORM', 'INPUT', 'BUTTON', 'SELECT', 'TEXTAREA',
  'SVG', 'MATH', 'TEMPLATE', 'PORTAL',
])

const NAMESPACE_HTML = 'http://www.w3.org/1999/xhtml'

/**
 * Attributi che avviano un caricamento di rete. Tutti sottostanno alla scelta sulle immagini
 * remote: se passasse anche uno solo, l'apertura del messaggio verrebbe confermata al
 * mittente, che e' esattamente cio' che le note legali dichiarano non accadere.
 * `href` rientra qui solo su <image> e <use>, dove indica una risorsa da caricare e non una
 * destinazione di navigazione.
 */
const ATTRIBUTI_RISORSA = new Set([
  'src', 'srcset', 'poster', 'data', 'background', 'lowsrc', 'dynsrc',
])

const TAG_HREF_RISORSA = new Set(['image', 'use'])

/** Vero quando il valore punta a un'origine remota, anche nella forma senza schema (//host). */
const SEMBRA_REMOTO = /(?:^|[\s,])(?:https?:)?\/\//i

/**
 * Schemi ammessi negli attributi che puntano a qualcosa; tutto il resto viene tolto.
 * `permettiRemoto` e' falso per la risorsa che non e' stata ancora richiesta: in quel caso
 * anche http e https vengono rifiutati.
 */
function indirizzoAmmesso(valore: string, permettiRemoto: boolean): boolean {
  const v = valore.trim().toLowerCase()
  if (v.startsWith('#')) return true
  if (v.startsWith('mailto:') || v.startsWith('tel:')) return true
  if (v.startsWith('data:image/')) return true
  if (v.startsWith('http://') || v.startsWith('https://')) return permettiRemoto
  return false
}

export type CorpoRipulito = { html: string; immaginiBloccate: number }

/**
 * Ripulisce il corpo HTML prima di mostrarlo.
 *
 * Due cose distinte. La prima: si tolgono script, iframe, object, i gestori di evento on*=
 * e ogni indirizzo con schema non ammesso, perche' il corpo arriva da mittenti esterni.
 * La seconda: si tolgono gli indirizzi delle risorse remote finche' chi legge non le chiede,
 * perche' il caricamento di un'immagine conferma al mittente che il messaggio e' stato aperto
 * e ne rivela indirizzo IP e momento della lettura. Le note legali dell'ERP dichiarano che
 * questo non avviene. La richiesta di mostrare le immagini ripristina src e poster remoti;
 * le immagini dichiarate negli stili restano rimosse, perche' url() viene tolto in ogni caso.
 *
 * Gli elementi fuori dal namespace HTML si rimuovono tutti, non solo <svg> e <math>: dentro
 * un sottoalbero straniero <script> e' un elemento come gli altri e il suo tagName resta
 * minuscolo, quindi un elenco di nomi in maiuscolo non lo intercetta. Tolti gli elementi
 * stranieri, la riserializzazione di body.innerHTML riguarda solo contenuto HTML e non puo'
 * far ricomparire attributi che il ciclo non ha esaminato.
 *
 * Il risultato va messo in srcdoc di un iframe, mai in dangerouslySetInnerHTML: con sandbox
 * vuoto, dentro l'iframe non gira nulla neppure se la ripulitura sbaglia.
 */
export function ripulisciHtml(html: string, mostraImmagini: boolean): CorpoRipulito {
  const documento = new DOMParser().parseFromString(html, 'text/html')
  let immaginiBloccate = 0

  for (const nodo of Array.from(documento.querySelectorAll('*'))) {
    // querySelectorAll ha fotografato l'albero prima delle rimozioni: un nodo gia' staccato
    // insieme a un antenato non va ne' ripulito ne' contato fra le risorse bloccate.
    if (!nodo.isConnected) continue

    const tag = nodo.localName.toLowerCase()
    if (nodo.namespaceURI !== NAMESPACE_HTML || TAG_RIMOSSI.has(tag.toUpperCase())) {
      nodo.remove()
      continue
    }

    // Una risorsa bloccata si conta una volta per elemento: src e srcset sullo stesso <img>
    // sono la stessa immagine non caricata.
    let risorsaBloccata = false

    for (const attributo of Array.from(nodo.attributes)) {
      const nome = attributo.name.toLowerCase()
      const valore = attributo.value

      if (nome.startsWith('on') || nome.startsWith('xlink')
          || nome === 'formaction' || nome === 'ping') {
        nodo.removeAttribute(attributo.name)
        continue
      }

      if (nome === 'style') {
        if (/url\s*\(/i.test(valore)) {
          nodo.setAttribute('style', valore.replace(/url\s*\([^)]*\)/gi, 'none'))
        }
        continue
      }

      if (ATTRIBUTI_RISORSA.has(nome) || (nome === 'href' && TAG_HREF_RISORSA.has(tag))) {
        // srcset e' un elenco di candidate con descrittori: non si convalida voce per voce in
        // modo affidabile, quindi si toglie sempre e l'immagine resta quella di src.
        const ammesso = nome !== 'srcset' && indirizzoAmmesso(valore, mostraImmagini)
        if (!ammesso) {
          nodo.removeAttribute(attributo.name)
          if (!mostraImmagini && SEMBRA_REMOTO.test(valore.trim())) risorsaBloccata = true
        }
        continue
      }

      if ((nome === 'href' || nome === 'action') && !indirizzoAmmesso(valore, true)) {
        nodo.removeAttribute(attributo.name)
      }
    }

    if (risorsaBloccata) immaginiBloccate++

    if (tag === 'a') {
      // Il sandbox non concede allow-popups: il collegamento non si apre, e l'indirizzo
      // resta leggibile nel suggerimento. Aprire il messaggio non deve poter portare altrove.
      const destinazione = nodo.getAttribute('href')
      nodo.setAttribute('target', '_blank')
      nodo.setAttribute('rel', 'noopener noreferrer nofollow')
      if (destinazione && !destinazione.startsWith('#')) nodo.setAttribute('title', destinazione)
    }
  }

  return { html: documento.body.innerHTML, immaginiBloccate }
}

/**
 * Il documento completo da passare a srcdoc. La Content-Security-Policy e' la seconda
 * barriera dopo la ripulitura: nega tutto e concede solo le immagini secondo la scelta
 * di chi legge.
 */
/** Tipo del messaggio che il corpo di un'email manda alla pagina quando si tocca un collegamento. */
export const MESSAGGIO_COLLEGAMENTO = 'cidis-collegamento'
/** Tipo del messaggio con l'altezza del corpo, che la pagina usa per allungare l'iframe (2/11/2026). */
export const MESSAGGIO_ALTEZZA = 'cidis-altezza'

export function paginaMessaggio(htmlRipulito: string, mostraImmagini: boolean): string {
  const immagini = mostraImmagini ? 'https: data:' : 'data:'
  // Lo script dei collegamenti, e nessun altro: consentito per impronta (packages/shared/src/script-collegamenti.ts).
  const politica = `default-src 'none'; img-src ${immagini}; style-src 'unsafe-inline'; `
    + `script-src 'sha256-${IMPRONTA_SCRIPT_COLLEGAMENTI}'; `
    + "font-src data:; form-action 'none'; frame-src 'none'; object-src 'none'"
  return '<!doctype html><html lang="it"><head><meta charset="utf-8">'
    + `<meta http-equiv="Content-Security-Policy" content="${politica}">`
    + `<script>${SCRIPT_COLLEGAMENTI_MESSAGGIO}</script>`
    + '<style>html{color-scheme:light}'
    + 'body{margin:12px;font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif;'
    + 'color:#14181d;background:#fff;word-break:break-word}'
    + 'img{max-width:100%;height:auto}table{max-width:100%}'
    + 'blockquote{margin:0 0 0 8px;padding-left:10px;border-left:2px solid #d5dae2;color:#4d555f}'
    + 'pre{white-space:pre-wrap}</style></head><body>'
    + htmlRipulito
    + '</body></html>'
}

/**
 * Testo citato in risposta. Se il messaggio ha solo il corpo HTML si cita il suo testo:
 * la risposta parte in testo semplice, non si reimposta il markup del mittente.
 */
export function citazione(messaggio: Messaggio): string {
  let corpo = messaggio.testo ?? ''
  if (!corpo && messaggio.html) {
    const documento = new DOMParser().parseFromString(messaggio.html, 'text/html')
    corpo = (documento.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim()
  }
  const righe = corpo.split('\n').map((r) => `> ${r}`).join('\n')
  return `\n\nIl ${dataEstesa(messaggio.data)} ${nomeBreve(messaggio.da)} ha scritto:\n${righe}\n`
}

// --- archivio delle caselle (507) ----------------------------------------------------------------

export type MotivoDismissione = 'persona' | 'indirizzo'

export const MOTIVI_DISMISSIONE: Record<MotivoDismissione, string> = {
  persona: 'La persona non lavora più con CIDIS',
  indirizzo: 'L’indirizzo non è più in uso',
}

export type StatoArchivio = 'caldo' | 'in_glacier' | 'glacier'
export type StatoRecupero = 'in_coda' | 'in_recupero' | 'pronto' | 'non_riuscito' | 'scaduto' | 'riportata'

export type RecuperoGlacier = {
  id: string
  tipo: 'casella' | 'messaggi'
  stato: StatoRecupero
  chiesto_il: string
  chiesto_da: string | null
  motivo: string
  giorni: number
  messaggi: number
  oggetti: number
  oggetti_pronti: number
  avviato_il: string | null
  pronto_il: string | null
  consultabile_fino_al: string | null
  riporta_chiesta_il: string | null
  errore: string | null
  stimato_il: string
  massimo_il: string
}

export type CasellaArchiviata = {
  casella_id: string
  indirizzo: string
  nome_visualizzato: string
  ambito: AmbitoCasella
  dismessa_motivo: MotivoDismissione | null
  archiviata_il: string
  archiviata_da: string | null
  glacier_dal: string
  giorni_a_glacier: number | null
  bloccata: { il: string; da: string | null; motivo: string } | null
  stato: StatoArchivio
  in_glacier_il: string | null
  messaggi: number
  estratti: number
  in_glacier: number
  risvegliata_fino_al: string | null
  recuperi: RecuperoGlacier[]
}

export type Archivio = {
  caselle: CasellaArchiviata[]
  tempi_glacier: { tipicoOre: number; massimoOre: number }
  giorni_risveglio: number
}

export type MessaggioInIndice = {
  id: string
  thread_id: string | null
  cartella: string | null
  direzione: 'entrata' | 'uscita'
  da: string
  a: string[]
  oggetto: string
  data: string
  ha_allegati: boolean
  estratto: boolean
  in_glacier: boolean
  in_recupero: boolean
  provenienza: Provenienza | null
}

export type PaginaIndice = { messaggi: MessaggioInIndice[]; prossimo: { data: string; id: string } | null }

export type MessaggioArchiviato = {
  id: string
  direzione: 'entrata' | 'uscita'
  da: string
  a: string[]
  cc: string[]
  oggetto: string
  data: string
  testo: string | null
  html: string | null
  testo_in_glacier: boolean
  estratto: boolean
  allegati: {
    id: string; nome: string; mime: string; dimensione: number; in_glacier: boolean; scaricabile: boolean
    /** Dalla 546: un infetto non si scarica dall'archivio; quelli in quarantena hanno la scritta. */
    antivirus?: EsitoAntivirus; quarantena?: QuarantenaAllegato | null
  }[]
  provenienza: Provenienza | null
}

export const archiviaCasella = (casellaId: string, motivo: MotivoDismissione) =>
  api.post<{ archiviata: boolean }>(`/posta/caselle/${casellaId}/archivia`, { motivo })

export const leggiArchivio = () => api.get<Archivio>('/posta/archivio')

export const indiceArchivio = (casellaId: string, primaDi?: { data: string; id: string } | null) =>
  api.get<PaginaIndice>(`/posta/archivio/${casellaId}/messaggi?${new URLSearchParams(
    primaDi ? { prima_di_data: primaDi.data, prima_di_id: primaDi.id } : {})}`)

export const messaggioArchiviato = (casellaId: string, postaId: string) =>
  api.get<MessaggioArchiviato>(`/posta/archivio/${casellaId}/messaggi/${postaId}`)

export const allegatoArchiviato = (casellaId: string, allegatoId: string) =>
  api.get<{ url: string }>(`/posta/archivio/${casellaId}/allegati/${allegatoId}`)

export const estendiArchivio = (casellaId: string, giorni: number, motivo: string) =>
  api.post<{ glacier_dal: string }>(`/posta/archivio/${casellaId}/estendi`, { giorni, motivo })

export const bloccaArchivio = (casellaId: string, motivo: string) =>
  api.post<{ bloccata: boolean }>(`/posta/archivio/${casellaId}/blocca`, { motivo })

export const sbloccaArchivio = (casellaId: string, motivo: string) =>
  api.post<{ bloccata: boolean }>(`/posta/archivio/${casellaId}/sblocca`, { motivo })

export const estraiDallArchivio = (casellaId: string, messaggi: string[], motivo: string) =>
  api.post<{ estratti: number; recupero_id: string | null; in_recupero: number }>(
    `/posta/archivio/${casellaId}/estrai`, { messaggi, motivo })

export const risvegliaCasella = (casellaId: string, giorni: number, motivo: string) =>
  api.post<{ recupero_id: string }>(`/posta/archivio/${casellaId}/risveglia`, { giorni, motivo })

export const riportaInArchivioCaldo = (casellaId: string) =>
  api.post<{ riporta: boolean }>(`/posta/archivio/${casellaId}/riporta`)

// --- quarantena degli allegati infetti (546) ------------------------------------------------------------

export type StatoRecuperoSicuro = 'in_coda' | 'in_recupero' | 'pronto' | 'scaduto' | 'non_riuscito' | 'annullato'

export type RecuperoSicuro = {
  id: string
  stato: StatoRecuperoSicuro
  chiesto_il: string
  chiesto_da: string
  /** Chiesto da chi legge: solo lui lo scarica. */
  mio: boolean
  motivo: string
  pronto_il: string | null
  zip_scade_il: string | null
  zip_dimensione: number | null
  impronta_file: string | null
  password_da_mostrare: boolean
  scaricamenti: number
  errore: string | null
  stimato_il: string
  massimo_il: string
}

export type VoceQuarantena = {
  id: string
  casella: string | null
  data_messaggio: string | null
  mittente: string | null
  nome: string
  mime: string
  dimensione: number
  messa_il: string
  messa_da: string | null
  origine_da_pulire: boolean
  eliminata: { il: string; da: string } | null
  recuperi: RecuperoSicuro[]
}

export type Quarantena = {
  voci: VoceQuarantena[]
  attiva: boolean
  tempi_glacier: { tipicoOre: number; massimoOre: number }
  ore_zip: number
}

export const leggiQuarantena = () => api.get<Quarantena>('/posta/quarantena')

export const eliminaDallaQuarantena = (id: string, nome: string) =>
  api.post<{ eliminata: boolean }>(`/posta/quarantena/${id}/elimina`, { nome })

export const chiediRecuperoSicuro = (id: string, motivo: string) =>
  api.post<{ id: string }>(`/posta/quarantena/${id}/recupero`, { motivo, consapevole: true })

export const scaricaRecuperoSicuro = (recuperoId: string) =>
  api.post<{ url: string; password: string | null; scade_il: string; nome_zip: string }>(
    `/posta/quarantena/recuperi/${recuperoId}/scarica`)

// --- programmi di posta (564) ------------------------------------------------------------------

export type StatoProgramma = 'in_attesa' | 'preparazione' | 'pronta' | 'rifiutata' | 'scaduta' | 'revocata'

export type CasellaPerProgramma = {
  casella: { id: string; indirizzo: string; nome_visualizzato: string; ambito: string }
  stato: StatoProgramma | null
  richiesta: {
    id: string; richiesta_il: string; motivo: string; approvazione_id: string; decisioni: number
    nota: string | null; revocata_il: string | null; motivo_revoca: string | null; pronta_il: string | null
  } | null
  avanzamento: number | null
  attesa_spazio: boolean
  credenziale: {
    id: string; creata_il: string; primo_accesso_il: string | null; ultimo_accesso_il: string | null
    ultimo_uso_il: string | null; origine: 'script' | 'gestionale'
  } | null
}

export type ProgrammiDiPosta = {
  server: string
  vede_tutte: boolean
  caselle: CasellaPerProgramma[]
}

export type ImpostazioniProgramma = {
  server: string
  imap: { porta: number; sicurezza: string }
  smtp: { porta: number; sicurezza: string }
  nome_utente: string
}

export type RichiestaProgramma = {
  id: string
  stato: StatoProgramma
  casella: { id: string; indirizzo: string; nome_visualizzato: string }
  persona: { id: string; nome: string; cognome: string; email: string }
  richiesta_il: string
  motivo: string
  approvazione_id: string
  decisioni: number
  avanzamento: number | null
  credenziale: { id: string; creata_il: string; primo_accesso_il: string | null; ultimo_uso_il: string | null } | null
  revocata_il: string | null
  motivo_revoca: string | null
}

export const programmiDiPosta = () => api.get<ProgrammiDiPosta>('/posta/programmi')

export const chiediProgramma = (casellaId: string, motivo: string) =>
  api.post<{ id: string; approvazione_id: string }>('/posta/programmi/richieste', { casella_id: casellaId, motivo })

export const passwordProgramma = (id: string) =>
  api.post<{ credenziale_id: string; password: string; impostazioni: ImpostazioniProgramma }>(`/posta/programmi/${id}/password`)

export const revocaProgramma = (id: string, motivo: string) =>
  api.post<{ revocata: boolean }>(`/posta/programmi/${id}/revoca`, { motivo })

export const tutteLeRichiesteProgramma = () =>
  api.get<{ puo_revocare: boolean; richieste: RichiestaProgramma[] }>('/posta/programmi/tutte')
